import type { KeyboardEventHandler } from "react"
import { useEffect, useMemo, useState } from "react"
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Link, useNavigate } from "@tanstack/react-router"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconGripDotsVerticalOutline18 as GripVertical,
} from "nucleo-ui-outline-18"

import { starter } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { Button, Dot, IntegrationCard } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { ConnectorSummary } from "./connectors/connector-status"
import type { DragEndEvent } from "@dnd-kit/core"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { useRequiredPlans } from "@/lib/plans"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { PlanPill } from "../plan-pill"
import { SettingsHeader } from "../settings-header"
import { SettingsSection } from "../settings-switches/settings-section"
import { ConnectorLogoTile } from "./connectors/connector-logo-tile"
import {
	connectorStatus,
	useConnectorSummaries,
} from "./connectors/connector-status"
import { CUSTOM_PRESETS, VENDOR_PRESETS } from "./connectors/presets"
import { McpActivityNavRow } from "./mcp-activity-list"
import { settingsNavGroupClass } from "./settings-nav-row"

interface ConnectorRow {
	id: string
	title: string
	url: string
	logoUrl: string | null
	type: "HTTP" | "MCP"
	availableToAgent: boolean
	allowedMcpTools: readonly string[]
}

export const SidebarSectionSettingsPage = () => {
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const navigate = useNavigate()
	const { passes: hasRequiredPlan } = useRequiredPlans(starter.andUp)
	const summaries = useConnectorSummaries()

	const sections: ConnectorRow[] = useMemo(
		() =>
			(workspace?.sidebarSections ?? []).map((section) => ({
				id: section.id,
				title: section.title,
				url: section.url,
				logoUrl: section.logoUrl ?? null,
				type: section.type,
				availableToAgent: section.availableToAgent,
				allowedMcpTools: section.allowedMcpTools ?? [],
			})),
		[workspace?.sidebarSections],
	)

	// Local order applied synchronously on drop, so the drop animation targets
	// the new slot instead of snapping once the synced query catches up.
	const [ordered, setOrdered] = useState<ConnectorRow[]>(sections)
	useEffect(() => setOrdered(sections), [sections])

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (!over || active.id === over.id) return
		const oldIndex = ordered.findIndex((s) => s.id === active.id)
		const newIndex = ordered.findIndex((s) => s.id === over.id)
		if (oldIndex === -1 || newIndex === -1) return
		const reordered = arrayMove(ordered, oldIndex, newIndex)
		setOrdered(reordered)
		zero.mutate(
			mutators.sidebarSections.reorder({
				sectionIds: reordered.map((s) => s.id),
			}),
		)
		analytics.track("sidebar-section-reorder")
	}

	const openSetup = (preset: string) => {
		analytics.track("create-sidebar-section", { preset })
		void navigate({
			to: "/settings/custom-connectors/new",
			search: { preset } as never,
		})
	}

	if (!workspace) return null

	return (
		<>
			<MetaInfo title="Connectors" />
			<SettingsHeader
				title="Connectors"
				subtitle="The agent answers account questions with data from Stripe or your own API."
				docsArticle="eb380251-0514-43ae-a638-3758a631617b"
				titleSuffix={<PlanPill plan="starter" />}
			/>

			{!hasRequiredPlan ? (
				<UpgradeButton required={starter.andUp} />
			) : (
				<>
					{ordered.length > 0 ? (
						<div className="space-y-4">
							<SettingsSection title="Connected" className="mt-0" />
							<div className={cn(settingsNavGroupClass, "overflow-visible")}>
								<DndContext
									sensors={sensors}
									collisionDetection={closestCenter}
									modifiers={[restrictToVerticalAxis]}
									onDragEnd={handleDragEnd}
								>
									<SortableContext
										items={ordered.map((s) => s.id)}
										strategy={verticalListSortingStrategy}
									>
										{ordered.map((section, index) => (
											<ConnectedRow
												key={section.id}
												section={section}
												summary={summaries.get(section.id) ?? null}
												isFirst={index === 0}
												isLast={index === ordered.length - 1}
											/>
										))}
									</SortableContext>
								</DndContext>
							</div>
							<McpActivityNavRow />
						</div>
					) : null}

					<RoleCheck not={["VIEWER"]}>
						<SettingsSection
							title="Add a connector"
							description="Connect your own API, any MCP server, or a tool below."
							className={ordered.length > 0 ? "mt-10" : "mt-0"}
						/>
						<div className="mt-4 grid grid-cols-1 gap-3 sm:-mx-3.5 sm:grid-cols-2">
							{CUSTOM_PRESETS.map((preset) => (
								<IntegrationCard
									key={preset.id}
									variant="grid"
									name={preset.title}
									description={preset.summary}
									logo={<preset.Logo className="h-5 w-5 text-primary" />}
									onClick={() => openSetup(preset.id)}
								/>
							))}
							{VENDOR_PRESETS.map((preset) => (
								<IntegrationCard
									key={preset.id}
									variant="grid"
									name={preset.title}
									description={preset.summary}
									logo={<preset.Logo className="h-6 w-6 text-primary" />}
									logoFill={preset.logoFillsTile}
									onClick={() => openSetup(preset.id)}
								/>
							))}
						</div>
						<p className="mt-4 text-xs tracking-tight text-secondary">
							HubSpot, Attio, PostgreSQL, Sentry, GitHub and Linear connect
							under{" "}
							<Link
								to="/settings/integrations"
								className="underline decoration-gray-300 underline-offset-2 hover:text-primary dark:decoration-gray-600"
							>
								Integrations
							</Link>
							.
						</p>
					</RoleCheck>
				</>
			)}
		</>
	)
}

function ConnectedRow({
	section,
	summary,
	isFirst,
	isLast,
}: {
	section: ConnectorRow
	summary: ConnectorSummary | null
	isFirst: boolean
	isLast: boolean
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: section.id })
	const navigate = useNavigate()
	const status = connectorStatus(section, summary)
	const href = `/settings/custom-connectors/${section.id}`

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.5 : 1,
				zIndex: isDragging ? 1 : undefined,
			}}
			className={cn(
				// The card stays overflow-visible for the drag handle, so the end rows
				// round their own hover. Passed in: dnd-kit renders nodes after the rows.
				"group/row relative flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50",
				isFirst && "rounded-t-[10px]",
				isLast && "rounded-b-[10px]",
				isDragging && "bg-white dark:bg-gray-850",
			)}
		>
			<RoleCheck not={["VIEWER"]}>
				<button
					type="button"
					aria-label={`Reorder ${section.title}`}
					className="absolute -left-5 top-1/2 -translate-y-1/2 cursor-move text-tertiary opacity-0 group-hover/row:opacity-100"
					{...attributes}
					{...listeners}
					onKeyDown={
						listeners?.onKeyDown as KeyboardEventHandler<HTMLButtonElement>
					}
				>
					<GripVertical className="h-3.5 w-3.5 flex-none" />
				</button>
			</RoleCheck>
			<Link to={href} className="flex min-w-0 flex-1 items-center gap-3">
				<ConnectorLogoTile
					url={section.url}
					logoUrl={section.logoUrl}
					type={section.type}
				/>
				<span className="flex min-w-0 flex-col gap-0.5">
					<span className="truncate text-sm font-medium text-primary">
						{section.title}
					</span>
					<span className="flex min-w-0 items-center gap-1.5 text-xs tracking-tight text-secondary">
						{status.tone !== "neutral" ? (
							<Dot
								color={status.tone === "ok" ? "green" : "red"}
								size="sm"
								className="flex-none"
							/>
						) : null}
						<span className="truncate">{status.line}</span>
					</span>
				</span>
			</Link>
			{status.signedOut ? (
				<RoleCheck not={["VIEWER"]}>
					<Button
						size="xs"
						onClick={() => void navigate({ href: `${href}#connection` })}
					>
						Reconnect
					</Button>
				</RoleCheck>
			) : summary && summary.calls > 0 ? (
				<span className="flex-none text-xs text-tertiary">
					{summary.calls.toLocaleString()}{" "}
					{summary.calls === 1 ? "call" : "calls"} this week
				</span>
			) : null}
			<Link
				to={href}
				aria-label={`Open ${section.title}`}
				className="flex-none"
			>
				<ChevronRight className="h-3.5 w-3.5 flex-none transform-gpu text-tertiary" />
			</Link>
		</div>
	)
}
